'use client';

import SyncIcon from '@mui/icons-material/Sync';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import {
	DataGridPro,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-pro';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { adaptGridValueCallback, isEmptyGridFilterValue } from '@parkchirp/ui-library';
import {
	AccessSystemIntegrationDisplayEnum,
	AccessSystemParkerActionDisplayEnum,
	AccessSystemParkerDisplayToPrismaEnum,
	AccessSystemParkerStatusDisplayEnum,
} from 'parkchirp-shared/enums';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';

import { SYNC_PARKER_IN_ACCESS_SYSTEM } from '../../mutations';
import { LIST_PARKER_ACCESS_SYSTEM_HISTORY } from '../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../types';
import { convertMuiFilterForV2Api, datagridFilterPanelAttributes, dayjs } from '../../utils';
import { DatagridLinearProgress } from '../loading';
import { UserModal } from '../modals/userModal';
import { PASHDetailModal, ParkerDetailModal } from './parkerDetail';

const style = {
	background: 'none',
	border: 0,
	color: 'blue',
	fontSize: 'inherit',
	cursor: 'pointer',
};

type AccessSystemHistoryTableProps = {
	tabNav?: React.JSX.Element;
	customFilters?: { field: string; op: string; value: any }[];
	setShowSnackbar: SetShowSnackbar;
	setShowDialog: SetShowDialog;
	csvExportButton: ((props: any) => ReactElement) | null;
	tenantSubscriptionId?: string;
	tenantAccountId?: number;
	theme?: any;
};

const dateOperators = getGridDateOperators();
const numericOperators = getGridNumericOperators();

export const AccessSystemHistoryTable = ({
	setShowDialog,
	setShowSnackbar,
	tabNav,
	tenantSubscriptionId,
	theme,
	// tenantAccountId,
}: AccessSystemHistoryTableProps) => {
	const defaultFilters: any = useMemo(
		() => [
			{
				propertyName: 'status',
				op: 'in',
				value: ['FAILED', 'PENDING', 'NEW'],
			},
		],
		[],
	);

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [rowCount, setRowCount] = useState(0);
	const [sortOrder, setSortOrder] = useState({ field: 'createdAt', sort: 'desc' });
	const [filters, setFilters] = useState(defaultFilters);
	const debouncedSetFilters = useDebounce(setFilters, 500);
	const [syncParker] = useMutation(SYNC_PARKER_IN_ACCESS_SYSTEM);
	const [syncingRowId, setSyncingRowId] = useState<string>('');

	const {
		loading,
		data: parkerAccessSystemHistoryData,
		refetch,
	} = useQuery(LIST_PARKER_ACCESS_SYSTEM_HISTORY, {
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			order: sortOrder,
			tenantSubscriptionId,
			filters,
		},
		context: { apiVersion: 'v2' },
		fetchPolicy: 'network-only', // Bypass Apollo's automated caching
	});

	const pashData = useMemo(() => {
		const hasData =
			parkerAccessSystemHistoryData?.listParkerAccessSystemHistory?.parkerAccessSystemHistory?.[0]?.parker;

		if (hasData) {
			return parkerAccessSystemHistoryData.listParkerAccessSystemHistory.parkerAccessSystemHistory.map(
				({ parker, createdByUser, ...pash }) => ({
					...pash,
					createdByUser,
					action: AccessSystemParkerActionDisplayEnum[pash.action],
					status: AccessSystemParkerStatusDisplayEnum[pash.status],
					firstName: parker.firstName,
					lastName: parker.lastName,
					parkerId: parker.id,
					facilityId: parker.subscription.billingProfile.facility.id,
					facilityName: parker.subscription.billingProfile.facility.facilityName,
					facilityNumber: parker.subscription.billingProfile.facility.facilityNumber,
					subscriptionId: parker.subscription.id,
					subscriptionName: parker.subscription.subscriptionName,
					accountId: parker.subscription.accountId,
					facilityTimezone: parker.subscription.billingProfile.facility.timezone,
					integration:
						AccessSystemIntegrationDisplayEnum[parker.subscription.billingProfile.facility.integration] ||
						parker.subscription.billingProfile.facility.integration,
					email: createdByUser?.email,
				}),
			);
		}
		return [];
	}, [parkerAccessSystemHistoryData]);

	useEffect(() => {
		if (!loading && parkerAccessSystemHistoryData) {
			setRowCount(parkerAccessSystemHistoryData?.listParkerAccessSystemHistory?.total);
		}
	}, [loading, parkerAccessSystemHistoryData]);

	useEffect(() => {
		setRowCount((prevRowCountState) => (rowCount !== undefined ? rowCount : prevRowCountState));
	}, [rowCount]);

	const handleSync = async (parkerId, pashId) => {
		setSyncingRowId(pashId);
		try {
			await syncParker({
				variables: {
					parkerId,
				},
				context: { apiVersion: 'v2' },
			});
			setShowSnackbar({
				message: 'Successfully Synced Parker In Access System',
				severity: 'success',
				autoHideDuration: 6000,
			});
			await refetch();
		} catch (err) {
			let error = 'Error Syncing Parker In Access System';
			if (err.message.includes('Access level code')) {
				error = err.message;
			}
			setShowSnackbar({
				message: error,
				severity: 'error',
				autoHideDuration: 6000,
			});
		} finally {
			setSyncingRowId('');
		}
	};

	const columns = [
		{
			field: 'actions',
			headerName: 'Actions',
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				if (syncingRowId === params.row.id) {
					return <CircularProgress />;
				}
				return (
					<Tooltip title='Sync parker in access system'>
						<IconButton
							disabled={syncingRowId && syncingRowId !== params.row.id}
							onClick={() => handleSync(params.row.parkerId, params.row.id)}
						>
							<SyncIcon />
						</IconButton>
					</Tooltip>
				);
			},
		},
		{
			field: 'createdAt',
			headerName: 'Created At',
			filterOperators: dateOperators,
			valueGetter: adaptGridValueCallback((params) => {
				if (!params.value) return null;
				return dayjs
					.utc(params.value)
					.tz(params.row.facilityTimezone || 'America/Chicago')
					.toDate();
			}),
			renderCell: (params) => {
				if (!params.value) return null;
				return dayjs(params.value)
					.tz(params.row.facilityTimezone || 'America/Chicago')
					.format('MM/DD/YYYY h:mm a z');
			},
			flex: 1.5,
		},
		{
			field: 'email',
			headerName: 'Created By',
			renderCell: ({ row }) =>
				row.createdByUser?.internalUseOnly ? (
					`${row.createdByUser.firstName} ${row.createdByUser.lastName}`
				) : (
					<a
						style={style}
						onClick={() =>
							setShowDialog(
								<UserModal
									dismissText='Close'
									user={row.createdByUser}
									handleDismiss={() => setShowDialog(null)}
								/>,
							)
						}
					>
						{row.email}
					</a>
				),
			flex: 1.5,
		},
		{
			field: 'lastName',
			headerName: 'Last Name',
			renderCell: (params) => (
				<a
					style={style}
					onClick={() =>
						setShowDialog(
							<ParkerDetailModal
								parkerId={params.row.parkerId}
								parkerFirstName={params.row.firstName}
								parkerLastName={params.row.lastName}
								parkerAccountId={params.row.accountId}
								parkerSubscriptionName={params.row.subscriptionName}
								facilityId={params.row.facilityId}
								parkerFacilityName={params.row.facilityName}
								parkerFacilityNumber={params.row.facilityNumber}
								setShowSnackbar={setShowSnackbar}
								subscriptionId={params.row.subscriptionId}
								tenantSubscriptionId={tenantSubscriptionId}
								handleClose={() => {
									refetch();
									setShowDialog(null);
								}}
								theme={theme}
							/>,
						)
					}
				>
					{params.row.lastName}
				</a>
			),
			flex: 1,
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			renderCell: (params) => (
				<a
					style={style}
					onClick={() =>
						setShowDialog(
							<ParkerDetailModal
								parkerId={params.row.parkerId}
								parkerFirstName={params.row.firstName}
								parkerLastName={params.row.lastName}
								parkerAccountId={params.row.accountId}
								parkerSubscriptionName={params.row.subscriptionName}
								facilityId={params.row.facilityId}
								parkerFacilityName={params.row.facilityName}
								parkerFacilityNumber={params.row.facilityNumber}
								setShowSnackbar={setShowSnackbar}
								subscriptionId={params.row.subscriptionId}
								tenantSubscriptionId={tenantSubscriptionId}
								handleClose={() => {
									refetch();
									setShowDialog(null);
								}}
								theme={theme}
							/>,
						)
					}
				>
					{params.row.firstName}
				</a>
			),
			flex: 1,
		},
		{
			field: 'integration',
			headerName: 'Integration',
			flex: 1,
			type: 'singleSelect' as const,
			valueOptions: ['Amano', 'Flash', 'Parkonect', 'Tiba', 'Get My Parking', 'IP Parking'],
		},
		{
			field: 'action',
			headerName: 'Action',
			type: 'singleSelect' as const,
			valueOptions: [
				'Activated Parker',
				'Reactivated Parker',
				'Removed Termination Date',
				'Scheduled Parker Activation',
				'Scheduled Parker Termination',
				'Suspended Parker',
				'Synced Parker',
				'Terminated Parker',
				'Updated Access Device',
				'Updated Parker',
				'Updated Parker Access Level',
				'Updated Parker Access System Status',
				'Block Facility Access',
				'Unblock Facility Access',
				'Set Parker To Neutral',
			],
			flex: 1,
		},
		{
			field: 'status',
			headerName: 'Status',
			type: 'singleSelect' as const,
			valueOptions: ['New', 'Pending', 'Failed', 'Success'],
			flex: 1,
			renderCell: (params) => {
				if (params.row.status === 'Failed') {
					return (
						<a
							style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
							onClick={() =>
								setShowDialog(
									<PASHDetailModal
										pashId={Number(params.row.id)}
										handleClose={() => {
											setShowDialog(null);
										}}
									/>,
								)
							}
						>
							{params.row.status}
						</a>
					);
				}

				return params.row.status;
			},
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			flex: 1,
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility Number',
			flex: 1,
		},
		{
			field: 'subscriptionName',
			headerName: 'Subscription Name',
			flex: 1,
		},
		{
			field: 'accountId',
			headerName: 'Account Id',
			filterOperators: numericOperators,
			flex: 1,
			renderCell: (params) => `AC-${params.row.accountId}`,
		},
	];

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		if (!sortModel.length) {
			return setSortOrder({ field: 'id', sort: 'asc' });
		}

		const { field, sort } = sortModel[0];

		return setSortOrder({ field, sort });
	}, []);

	const onFilterModelChange = useCallback(
		(filterModel: GridFilterModel) => {
			const { items: filterItems } = filterModel;

			if (!filterItems.length) {
				debouncedSetFilters([]);
				return;
			}

			const queryFilters = filterItems
				.map((filter) => {
					const { field, operator, value: rawValue } = filter;
					if (isEmptyGridFilterValue(rawValue) && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];
					const { op, value }: { op: string; value: any } = convertMuiFilterForV2Api(operator, rawValue);
					if (field === 'action' || field === 'status' || field === 'integration') {
						let newValue;
						if (typeof value === 'string') {
							newValue = AccessSystemParkerDisplayToPrismaEnum[value];
						} else {
							newValue = value.map((v) => AccessSystemParkerDisplayToPrismaEnum[v]);
						}
						return {
							propertyName: field,
							op,
							value: newValue,
						};
					}

					return {
						propertyName: field,
						op,
						value,
					};
				})
				.flat();
			debouncedSetFilters(queryFilters);
		},
		[debouncedSetFilters],
	);

	const renderCustomToolbar = () => (
		<GridToolbarContainer>
			<GridToolbarFilterButton />
			<GridToolbarColumnsButton />
			<GridToolbarDensitySelector />
		</GridToolbarContainer>
	);

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			{tabNav}
			<div className='pca__datagrid-sticky--headfoot' data-cy='access-devices-datagrid-container'>
				<DataGridPro
					sx={{ height: '100%' }}
					paginationModel={{ page, pageSize }}
					onSortModelChange={onSortModelChange}
					filterMode='server'
					onFilterModelChange={onFilterModelChange}
					hideFooter={false}
					hideFooterPagination={false}
					onPaginationModelChange={setPaginationModel}
					pagination
					rows={pashData || []}
					rowCount={rowCount}
					columns={columns}
					pageSizeOptions={[pageSize]}
					paginationMode='server'
					sortingMode='server'
					loading={loading}
					slots={{
						toolbar: renderCustomToolbar,
						loadingOverlay: DatagridLinearProgress,
					}}
					slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
					initialState={{
						filter: {
							filterModel: {
								items: [
									{
										id: 1,
										field: 'status',
										operator: 'isAnyOf',
										value: ['Failed', 'Pending', 'New'],
									},
								],
							},
						},
					}}
				/>
			</div>
		</div>
	);
};
